import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Grid,
  Input,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography
} from 'antd'
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  FilePdfOutlined,
  InboxOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { goodsReceiptsAPI, formatCurrency, formatDate, handleAPIError } from '../../services/PurchaseService'
import purchasePrintService from '../../services/purchasePrintService'
import './GoodsReceiptsManagement.css'

const { Text, Title } = Typography
const { RangePicker } = DatePicker
const { useBreakpoint } = Grid

const STATUS_META = {
  draft: { color: 'default', label: 'ฉบับร่าง' },
  pending: { color: 'gold', label: 'รอยืนยัน' },
  confirmed: { color: 'green', label: 'รับเข้าคลังแล้ว' },
  posted: { color: 'blue', label: 'ลงบัญชีแล้ว' },
  cancelled: { color: 'red', label: 'ยกเลิก' }
}

const GoodsReceiptsManagement = () => {
  const { message } = App.useApp()
  const navigate = useNavigate()
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState()
  const [dateRange, setDateRange] = useState(null)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 })
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState('')
  const [selectedReceipt, setSelectedReceipt] = useState(null)

  const loadReceipts = useCallback(async (page = 1) => {
    setLoading(true)
    try {
      const params = { page, limit: pagination.pageSize, search: search.trim() || undefined, status }
      if (dateRange?.[0]) params.date_from = dateRange[0].format('YYYY-MM-DD')
      if (dateRange?.[1]) params.date_to = dateRange[1].format('YYYY-MM-DD')
      const response = await goodsReceiptsAPI.getAll(params)
      setRows(response.data || [])
      setPagination(current => ({ ...current, current: page, total: response.pagination?.total || 0 }))
    } catch (error) {
      message.error(handleAPIError(error))
    } finally {
      setLoading(false)
    }
  }, [dateRange, message, pagination.pageSize, search, status])

  useEffect(() => {
    loadReceipts(1)
  }, [loadReceipts])

  const summary = useMemo(() => ({
    count: pagination.total,
    confirmed: rows.filter(item => ['confirmed', 'posted'].includes(item.status)).length,
    draft: rows.filter(item => ['draft', 'pending'].includes(item.status)).length,
    value: rows.reduce((sum, item) => sum + Number(item.total_received_value || item.total_amount || 0), 0)
  }), [pagination.total, rows])

  const showPreview = async record => {
    setSelectedReceipt(record)
    setPreviewOpen(true)
    setPreviewLoading(true)
    setPreviewUrl('')
    try {
      const response = await goodsReceiptsAPI.getById(record.id)
      const detail = response.data
      setSelectedReceipt(detail)
      setPreviewUrl(await purchasePrintService.generateReceiptPDF(detail))
    } catch (error) {
      message.error(`สร้างตัวอย่างเอกสารไม่สำเร็จ: ${handleAPIError(error)}`)
      setPreviewOpen(false)
    } finally {
      setPreviewLoading(false)
    }
  }

  const confirmReceipt = async record => {
    try {
      setLoading(true)
      await goodsReceiptsAPI.confirm(record.id)
      message.success(`ยืนยัน ${record.receipt_number} และเพิ่มสินค้าเข้าคลังแล้ว`)
      await loadReceipts(pagination.current)
    } catch (error) {
      message.error(handleAPIError(error))
    } finally {
      setLoading(false)
    }
  }

  const statusTag = value => {
    const meta = STATUS_META[value] || { color: 'default', label: value || '-' }
    return <Tag color={meta.color}>{meta.label}</Tag>
  }

  const sourceTag = record => record.source_type === 'direct' || !record.purchase_id
    ? <Tag color="cyan">รับสินค้าโดยตรง</Tag>
    : <Tag color="geekblue">อ้างอิงใบสั่งซื้อ</Tag>

  const actionButtons = record => (
    <Space size="small">
      <Button icon={<EyeOutlined />} onClick={() => showPreview(record)}>ดูเอกสาร</Button>
      {['draft', 'pending'].includes(record.status) && (
        <Popconfirm
          title="ยืนยันรับสินค้าเข้าคลัง?"
          description="ระบบจะเพิ่มล็อต สต็อก และสร้าง Draft Journal หลังยืนยัน"
          okText="ยืนยันรับเข้า"
          cancelText="ยกเลิก"
          onConfirm={() => confirmReceipt(record)}
        >
          <Button type="primary" icon={<CheckCircleOutlined />}>ยืนยันรับเข้า</Button>
        </Popconfirm>
      )}
    </Space>
  )

  const columns = [
    { title: 'เลขที่ใบรับสินค้า', dataIndex: 'receipt_number', fixed: 'left', width: 175, render: value => <Text strong>{value}</Text> },
    { title: 'วันที่รับ', dataIndex: 'receipt_date', width: 120, render: formatDate },
    { title: 'แหล่งที่มา', key: 'source_type', width: 160, render: (_, record) => sourceTag(record) },
    { title: 'อ้างอิง PO', dataIndex: 'purchase_number', width: 150, render: value => value || '-' },
    { title: 'ผู้จำหน่าย', dataIndex: 'supplier_name', width: 220 },
    { title: 'จำนวนรายการ', dataIndex: 'item_count', align: 'center', width: 110, render: value => `${value || 0} รายการ` },
    { title: 'มูลค่ารับเข้า', dataIndex: 'total_received_value', align: 'right', width: 150, render: formatCurrency },
    { title: 'สถานะ', dataIndex: 'status', width: 130, render: statusTag },
    { title: 'การดำเนินการ', key: 'actions', fixed: 'right', width: 230, render: (_, record) => actionButtons(record) }
  ]

  return (
    <div className="goods-receipts-page">
      <div className="goods-receipts-header">
        <div>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/purchases')}>ไปหน้าใบสั่งซื้อ</Button>
          <Title level={2}><InboxOutlined /> ใบรับสินค้าเข้าคลัง</Title>
          <Text type="secondary">รับสินค้าโดยตรงด้วยการเลือกสินค้า/ยิงบาร์โค้ด หรือดึงรายการจากใบสั่งซื้อ และเพิ่มล็อต/สต็อกเมื่อยืนยัน</Text>
        </div>
        <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => navigate('/purchases/goods-receipt/create')}>
          สร้างใบรับสินค้า
        </Button>
      </div>

      <Row gutter={[12, 12]} className="goods-receipts-summary">
        <Col xs={12} lg={6}><Card><Statistic title="ใบรับสินค้าทั้งหมด" value={summary.count} prefix={<InboxOutlined />} /></Card></Col>
        <Col xs={12} lg={6}><Card><Statistic title="รับเข้าคลังแล้ว (หน้านี้)" value={summary.confirmed} valueStyle={{ color: '#389e0d' }} /></Card></Col>
        <Col xs={12} lg={6}><Card><Statistic title="รอยืนยัน (หน้านี้)" value={summary.draft} valueStyle={{ color: '#d48806' }} /></Card></Col>
        <Col xs={12} lg={6}><Card><Statistic title="มูลค่ารับเข้า (หน้านี้)" value={summary.value} precision={2} suffix="บาท" /></Card></Col>
      </Row>

      <Card className="goods-receipts-filter">
        <Row gutter={[10, 10]} align="middle">
          <Col xs={24} md={7}><Input prefix={<SearchOutlined />} allowClear value={search} onChange={event => setSearch(event.target.value)} onPressEnter={() => loadReceipts(1)} placeholder="เลขที่ใบรับสินค้า, PO หรือผู้จำหน่าย" /></Col>
          <Col xs={12} md={4}><Select allowClear value={status} onChange={setStatus} placeholder="ทุกสถานะ" style={{ width: '100%' }} options={Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label }))} /></Col>
          <Col xs={24} md={7}><RangePicker value={dateRange} onChange={setDateRange} format="DD/MM/YYYY" style={{ width: '100%' }} /></Col>
          <Col xs={24} md={6}><Space><Button type="primary" icon={<SearchOutlined />} onClick={() => loadReceipts(1)}>ค้นหา</Button><Button icon={<ReloadOutlined />} onClick={() => { setSearch(''); setStatus(undefined); setDateRange(null) }}>ล้างตัวกรอง</Button></Space></Col>
        </Row>
      </Card>

      <Card className="goods-receipts-list" title={<Space><ShoppingCartOutlined />ประวัติการรับสินค้า</Space>}>
        {isMobile ? (
          <List
            loading={loading}
            dataSource={rows}
            locale={{ emptyText: <Empty description="ยังไม่มีใบรับสินค้า" /> }}
            renderItem={record => (
              <List.Item className="goods-receipt-mobile-card">
                <div className="goods-receipt-mobile-top"><div><Text strong>{record.receipt_number}</Text><br /><Text type="secondary">{formatDate(record.receipt_date)}</Text></div>{statusTag(record.status)}</div>
                <div className="goods-receipt-mobile-supplier"><Text>{record.supplier_name || '-'}</Text><Space size={4}>{sourceTag(record)}{record.purchase_number && <Text type="secondary">{record.purchase_number}</Text>}</Space></div>
                <div className="goods-receipt-mobile-value"><Text type="secondary">{record.item_count || 0} รายการ</Text><Text strong>{formatCurrency(record.total_received_value || record.total_amount)}</Text></div>
                <div className="goods-receipt-mobile-actions">{actionButtons(record)}</div>
              </List.Item>
            )}
            pagination={{ current: pagination.current, pageSize: pagination.pageSize, total: pagination.total, simple: true, onChange: loadReceipts }}
          />
        ) : (
          <Table
            rowKey="id"
            loading={loading}
            dataSource={rows}
            columns={columns}
            scroll={{ x: 1250 }}
            pagination={{ ...pagination, showSizeChanger: false, showTotal: total => `${total} รายการ` }}
            onChange={page => loadReceipts(page.current)}
          />
        )}
      </Card>

      <Modal
        title={<Space><FilePdfOutlined />ใบรับสินค้าเข้าคลัง {selectedReceipt?.receipt_number}</Space>}
        open={previewOpen}
        onCancel={() => { setPreviewOpen(false); setPreviewUrl('') }}
        footer={null}
        width="min(1100px, 96vw)"
        className="goods-receipt-preview-modal"
      >
        {previewLoading ? <div className="goods-receipt-preview-loading"><Spin size="large" tip="กำลังสร้างเอกสาร A4..." /></div> : previewUrl && (
          <iframe title="ตัวอย่างใบรับสินค้าเข้าคลัง" src={previewUrl} />
        )}
      </Modal>
    </div>
  )
}

export default GoodsReceiptsManagement
